/**
 * Training (#190): what the bar teaches its own staff.
 *
 * Three states in one screen, because they are three steps of one job:
 *
 *   the course   the modules, each with where this person has got to
 *   a module     its pages one at a time, then the check
 *   the roster   who has done what, for a manager
 *
 * A manager writes the modules here too, in an editor that is the same
 * pages the other way round. What a module is and how a check is marked
 * are `training.ts` in @baros/core; the marking happens on the server and
 * nothing in this folder ever knows an answer.
 */

import { useCallback, useEffect, useState } from "react";
import {
  drinksOf,
  lineText,
  recipeName,
  type Ingredient,
  type Recipe,
  type TrainingModule,
  type TrainingPage,
  type TrainingRow,
} from "@baros/core";
import { ApiError } from "../api.js";
import { useApp } from "../context.js";
import type { ScreenProps } from "../screens.js";
import { trainingApi, type MarkedCheck, type ModuleView, type Paper } from "./api.js";
import { ModuleEditor } from "./ModuleEditor.js";
import "./training.css";

const message = (err: unknown): string =>
  err instanceof ApiError ? err.message : err instanceof Error ? err.message : "Something went wrong";

const STATE_LABEL: Readonly<Record<string, string>> = {
  "not-started": "Not started",
  reading: "In progress",
  check: "Ready for the check",
  passed: "Passed",
};

export function TrainingScreen({ params }: ScreenProps) {
  const app = useApp();
  const { token } = app;
  const openId = params["moduleId"] ?? null;
  const editing = params["edit"] === "1";

  const [course, setCourse] = useState<readonly TrainingRow[] | null>(null);
  const [canEdit, setCanEdit] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const res = await trainingApi.course(token);
      setCourse(res.course ?? []);
      setCanEdit(res.canEdit === true);
    } catch (err) {
      setError(message(err));
    }
  }, [token]);

  useEffect(() => {
    void load();
  }, [load]);

  const go = (moduleId: string | null, edit = false) =>
    app.go({
      kind: "training",
      params: moduleId ? { moduleId, ...(edit ? { edit: "1" } : {}) } : {},
    });

  if (openId) {
    return editing && canEdit ? (
      <ModuleEditor
        moduleId={openId === "new" ? null : openId}
        onDone={async (saved) => {
          await load();
          go(saved);
        }}
        onCancel={() => go(openId === "new" ? null : openId)}
      />
    ) : (
      <ModuleRun moduleId={openId} canEdit={canEdit} onBack={() => void load().then(() => go(null))} onEdit={() => go(openId, true)} />
    );
  }

  return (
    <Course
      course={course}
      canEdit={canEdit}
      error={error}
      onRetry={() => void load()}
      onOpen={(moduleId) => go(moduleId)}
      onNew={() => go("new", true)}
    />
  );
}

// ------------------------------------------------------------- the course

function Course(p: {
  readonly course: readonly TrainingRow[] | null;
  readonly canEdit: boolean;
  readonly error: string | null;
  readonly onRetry: () => void;
  readonly onOpen: (moduleId: string) => void;
  readonly onNew: () => void;
}) {
  const rows = p.course ?? [];
  const passed = rows.filter((r) => r.standing.state === "passed").length;
  const next = rows.find((r) => r.standing.state !== "passed") ?? null;

  if (p.error) {
    return (
      <div className="pad">
        <div className="msg err">
          {p.error}
          <div style={{ marginTop: 10 }}>
            <button className="btn sm" onClick={p.onRetry}>Try again</button>
          </div>
        </div>
      </div>
    );
  }

  if (!p.course) return <div className="pad label">Reading the course…</div>;

  return (
    <div className="screen">
      <div className="row screen-head tr-head">
        <div>
          <div className="label">{rows.length === 0 ? "nothing written yet" : `${passed} of ${rows.length} passed`}</div>
          <div className="tr-count">{next ? next.module.title : rows.length === 0 ? "Empty" : "All done"}</div>
          {next && <div className="label tr-next">{STATE_LABEL[next.standing.state]}</div>}
        </div>
        <span className="spacer" />
        {p.canEdit && (
          <button className="btn dashed tr-new" onClick={p.onNew}>
            + New module
          </button>
        )}
      </div>

      {rows.length === 0 ? (
        <div className="pad">
          <div className="msg">
            <div style={{ fontWeight: 600 }}>No training written yet.</div>
            <div style={{ color: "var(--t2)", marginTop: 4 }}>
              {p.canEdit
                ? "A module is pages about how this bar runs — opening, the list, the greeting — with a few questions at the end."
                : "There is nothing to work through yet. A manager writes these."}
            </div>
          </div>
        </div>
      ) : (
        <div className="scroll tr-list">
          {rows.map(({ module, standing }) => (
            <button key={module.moduleId} className="tr-card" onClick={() => p.onOpen(module.moduleId)}>
              <div className="tr-card-top">
                <span className="tr-card-title">{module.title}</span>
                {module.status === "draft" && <span className="label tag-pill">Draft</span>}
              </div>
              {module.summary && <div className="tr-card-sum">{module.summary}</div>}
              <div className="tr-bar" aria-hidden="true">
                <span style={{ width: `${Math.round(standing.through * 100)}%` }} />
              </div>
              <div className="label tr-card-foot">
                <span className={standing.state === "passed" ? "pos" : undefined}>{STATE_LABEL[standing.state]}</span>
                <span>
                  {standing.pages} {standing.pages === 1 ? "page" : "pages"}
                  {module.questions.length > 0 || standing.state !== "passed" ? "" : ""}
                  {standing.best !== null ? ` · best ${Math.round(standing.best)}%` : ""}
                </span>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ----------------------------------------------------------- one module

type Phase = { readonly kind: "reading"; readonly at: number } | { readonly kind: "check" } | { readonly kind: "marked"; readonly result: MarkedCheck };

function ModuleRun(p: {
  readonly moduleId: string;
  readonly canEdit: boolean;
  readonly onBack: () => void;
  readonly onEdit: () => void;
}) {
  const app = useApp();
  const { token } = app;
  const [view, setView] = useState<ModuleView | null>(null);
  const [phase, setPhase] = useState<Phase>({ kind: "reading", at: 0 });
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    setView(null);
    trainingApi
      .module(token, p.moduleId)
      .then((res) => {
        if (!live) return;
        setView(res);
        // Pick up where they left off, but never past the last page.
        const at = Math.min(res.progress?.page ?? 0, Math.max(0, (res.module.pages ?? []).length - 1));
        setPhase({ kind: "reading", at: Math.max(0, at) });
      })
      .catch((err) => live && setError(message(err)));
    return () => {
      live = false;
    };
  }, [token, p.moduleId]);

  if (error) return <div className="pad"><div className="msg err">{error}</div></div>;
  if (!view) return <div className="pad label">Opening…</div>;

  const pages = view.module.pages ?? [];
  const title = view.module.title;

  return (
    <div className="screen tr-run">
      <div className="row screen-head tr-head">
        <div style={{ minWidth: 0 }}>
          <button className="label tr-back" onClick={p.onBack}>← All training</button>
          <h2 className="tr-title">{title}</h2>
        </div>
        <span className="spacer" />
        {p.canEdit && (
          <button className="btn sm" onClick={p.onEdit}>Edit</button>
        )}
      </div>

      {phase.kind === "reading" && pages.length > 0 && (
        <Reading
          module={view.module}
          at={phase.at}
          questions={view.questions}
          onMove={(at) => {
            setPhase({ kind: "reading", at });
            // The furthest read, recorded as they go, so a shift that ends
            // mid-module does not lose the morning.
            void trainingApi.read(token, p.moduleId, at + 1).catch(() => {});
          }}
          onCheck={() => setPhase({ kind: "check" })}
          onDone={() => {
            void trainingApi.read(token, p.moduleId, pages.length).catch(() => {});
            p.onBack();
          }}
        />
      )}

      {phase.kind === "reading" && pages.length === 0 && (
        <div className="pad">
          <div className="msg">This module has no pages yet.</div>
        </div>
      )}

      {phase.kind === "check" && (
        <Check
          moduleId={p.moduleId}
          onMarked={(result) => setPhase({ kind: "marked", result })}
          onBack={() => setPhase({ kind: "reading", at: Math.max(0, pages.length - 1) })}
        />
      )}

      {phase.kind === "marked" && (
        <Marked
          result={phase.result}
          onAgain={() => setPhase({ kind: "check" })}
          onBack={p.onBack}
        />
      )}
    </div>
  );
}

function Reading(p: {
  readonly module: TrainingModule;
  readonly at: number;
  readonly questions: number;
  readonly onMove: (at: number) => void;
  readonly onCheck: () => void;
  readonly onDone: () => void;
}) {
  const pages = p.module.pages;
  const page = pages[p.at];
  const last = p.at >= pages.length - 1;
  if (!page) return null;

  return (
    <>
      <div className="scroll tr-page">
        <div className="label tr-step">
          Page {p.at + 1} of {pages.length}
          {page.heading ? ` · ${page.heading}` : ""}
        </div>
        <PageBody page={page} />
      </div>

      <div className="tr-foot">
        <button className="btn" disabled={p.at === 0} onClick={() => p.onMove(p.at - 1)}>
          ← Back
        </button>
        <div className="tr-dots" aria-hidden="true">
          {pages.map((pg, i) => (
            <span key={pg.pageId} className={i <= p.at ? "on" : undefined} />
          ))}
        </div>
        {last ? (
          p.questions > 0 ? (
            <button className="btn primary" onClick={p.onCheck}>
              Take the check ({p.questions}) →
            </button>
          ) : (
            <button className="btn primary" onClick={p.onDone}>
              Finish →
            </button>
          )
        ) : (
          <button className="btn primary" onClick={() => p.onMove(p.at + 1)}>
            Next →
          </button>
        )}
      </div>
    </>
  );
}

/** One page, drawn by its kind. A drinks page reads the book as it is now. */
function PageBody({ page }: { readonly page: TrainingPage }) {
  const app = useApp();

  if (page.kind === "prose") {
    return <Prose body={page.body} />;
  }

  if (page.kind === "image") {
    return <Picture imageKey={page.imageKey} caption={page.caption} />;
  }

  const recipes = app.library.recipes ?? [];
  const items = new Map((app.library.items ?? []).map((i) => [i.itemId, i]));
  const ingredients: ReadonlyMap<string, Ingredient> = new Map(
    app.library.ingredients.map((i) => [i.ingredientId, i]),
  );
  const shown = drinksOf(page, recipes, (r) => r.tags ?? []);
  const nameOf = (r: Recipe) => {
    const item = items.get(r.itemId);
    return item ? recipeName(item, r) : r.label;
  };

  return (
    <>
      {page.body && <Prose body={page.body} />}
      {shown.length === 0 ? (
        <div className="label tr-none">No drinks on file for this page yet.</div>
      ) : (
        <div className="tr-drinks">
          {shown.map((recipe) => (
            <article className="tr-drink" key={recipe.recipeId}>
              <h3>{nameOf(recipe)}</h3>
              {recipe.talk?.one && <p className="tr-one">{recipe.talk.one}</p>}
              <ol>
                {recipe.lines.map((l, i) => (
                  <li key={i}>{lineText(l, ingredients)}</li>
                ))}
              </ol>
              <div className="label">
                {[recipe.glass, recipe.method, recipe.ice].map((s) => s.trim()).filter(Boolean).join(" · ")}
                {recipe.garnish.trim() ? ` · ${recipe.garnish.trim()}` : ""}
              </div>
            </article>
          ))}
        </div>
      )}
    </>
  );
}

/** Paragraphs, as the manager typed them. Blank lines separate. */
function Prose({ body }: { readonly body: string }) {
  return (
    <>
      {body
        .split(/\n{2,}/)
        .map((para) => para.trim())
        .filter(Boolean)
        .map((para, i) => (
          <p className="tr-para" key={i}>
            {para}
          </p>
        ))}
    </>
  );
}

/**
 * A picture, on a link that expires.
 *
 * The key is stored on the page; the link is signed when it is shown, so
 * nothing durable in the page or the address bar reaches the bucket.
 */
function Picture({ imageKey, caption }: { readonly imageKey: string; readonly caption: string }) {
  const { token } = useApp();
  const [src, setSrc] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let live = true;
    setSrc(null);
    setFailed(false);
    trainingApi
      .imageUrl(token, imageKey)
      .then((res) => live && setSrc(res.url))
      .catch(() => live && setFailed(true));
    return () => {
      live = false;
    };
  }, [token, imageKey]);

  if (failed) return <div className="label tr-none">That picture could not be loaded. {caption}</div>;
  return (
    <figure className="tr-figure">
      {src ? <img src={src} alt={caption} /> : <div className="tr-figure-wait label">Loading the picture…</div>}
      <figcaption className="label">{caption}</figcaption>
    </figure>
  );
}

// ------------------------------------------------------------- the check

function Check(p: {
  readonly moduleId: string;
  readonly onMarked: (result: MarkedCheck) => void;
  readonly onBack: () => void;
}) {
  const { token } = useApp();
  const [paper, setPaper] = useState<Paper | null>(null);
  const [picked, setPicked] = useState<Record<string, number[]>>({});
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    trainingApi
      .check(token, p.moduleId)
      .then((res) => live && setPaper(res))
      .catch((err) => live && setError(message(err)));
    return () => {
      live = false;
    };
  }, [token, p.moduleId]);

  if (error) return <div className="pad"><div className="msg err">{error}</div></div>;
  if (!paper) return <div className="pad label">Setting the check…</div>;

  const toggle = (questionId: string, at: number, many: boolean) =>
    setPicked((held) => {
      const was = held[questionId] ?? [];
      if (!many) return { ...held, [questionId]: was[0] === at ? [] : [at] };
      return { ...held, [questionId]: was.includes(at) ? was.filter((n) => n !== at) : [...was, at] };
    });

  const answered = paper.asked.filter((q) => (picked[q.questionId] ?? []).length > 0).length;

  return (
    <>
      <div className="scroll tr-page">
        <div className="label tr-step">
          The check · {paper.asked.length} {paper.asked.length === 1 ? "question" : "questions"} · pass at{" "}
          {paper.passMark}%
        </div>
        {paper.asked.map((question, n) => (
          <fieldset className="tr-q" key={question.questionId}>
            <legend>
              <span className="label">{n + 1}</span> {question.prompt}
            </legend>
            {question.many && <div className="label tr-many">Pick every one that is in it.</div>}
            {question.choices.map((choice, at) => {
              const on = (picked[question.questionId] ?? []).includes(at);
              return (
                <label className={`tr-choice${on ? " on" : ""}`} key={at}>
                  <input
                    type={question.many ? "checkbox" : "radio"}
                    name={question.questionId}
                    checked={on}
                    onChange={() => toggle(question.questionId, at, question.many)}
                  />
                  <span>{choice}</span>
                </label>
              );
            })}
          </fieldset>
        ))}
      </div>

      <div className="tr-foot">
        <button className="btn" onClick={p.onBack} disabled={sending}>
          ← Back to the pages
        </button>
        <span className="label">
          {answered} of {paper.asked.length} answered
        </span>
        <button
          className="btn primary"
          disabled={sending || paper.asked.length === 0}
          onClick={async () => {
            setSending(true);
            setError(null);
            try {
              p.onMarked(await trainingApi.mark(token, p.moduleId, paper.seed, picked));
            } catch (err) {
              setError(message(err));
            } finally {
              setSending(false);
            }
          }}
        >
          {sending ? "Marking…" : "Hand it in →"}
        </button>
      </div>
    </>
  );
}

function Marked(p: {
  readonly result: MarkedCheck;
  readonly onAgain: () => void;
  readonly onBack: () => void;
}) {
  const marked = p.result.marked;
  const why = p.result.why ?? {};
  if (!marked) return null;
  const percent = Math.round(marked.percent);

  return (
    <>
      <div className="scroll tr-page">
        <div className={`tr-result${marked.passed ? " pass" : ""}`}>
          <div className="label">{marked.passed ? "Passed" : "Not this time"}</div>
          <div className="tr-score">{percent}%</div>
          <div className="label">
            {marked.right} of {marked.asked} right
          </div>
        </div>
        {marked.wrong.length > 0 && (
          <>
            <div className="label tr-step">What to go back over</div>
            {marked.wrong.map((questionId) => (
              <p className="tr-para" key={questionId}>
                {why[questionId] ?? "Have another read of the pages for this one."}
              </p>
            ))}
          </>
        )}
      </div>
      <div className="tr-foot">
        <button className="btn" onClick={p.onBack}>← All training</button>
        <span className="spacer" />
        {!marked.passed && (
          <button className="btn primary" onClick={p.onAgain}>
            Try the check again →
          </button>
        )}
      </div>
    </>
  );
}
